import { useState, useMemo, useCallback } from 'react'

import { useFirestoreConnect } from 'react-redux-firebase'
import { useSelector } from 'react-redux'
import firebase from 'firebase/app'
import moment from 'moment'

import { DataGrid, GridCellParams, GridColumns, GridSortItem } from '@material-ui/data-grid'
import {
    Avatar,
    ListItem,
    ListItemText,
    ListItemAvatar,
    makeStyles,
    Typography,
    Box,
    IconButton,
    Tooltip
} from '@material-ui/core'
import { Delete, Email } from '@material-ui/icons'

import {
    SearchField,
    ConfirmationDialog,
    PermissionsDialog,
    getInviteDefaults
} from 'UI/Components'

import { removeUserFromOrg, logToAnalytics, updatePendingUserInvitationTime } from 'modules'

import {
    getRoleName,
    ROLE,
    useAuth,
    useDataMutation,
    useNotification,
    useOrganization,
    useRoles,
    useServiceUsage
} from 'hooks'

import { OrganizationUserType } from 'app/types'
import { ExceededUsersNotification } from '../ExceededUsersNotification'

const useStyles = makeStyles(() => ({
    grid: {
        '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
            outline: 'none!important'
        },
        '& .MuiDataGrid-cell': {
            cursor: 'pointer'
        }
    },
    title: {
        fontSize: '24px',
        fontWeight: 400
    },
    subTitle: {
        fontSize: '11px'
    },
    searchAndUsersAmount: {
        display: 'flex'
    },
    search: {
        width: '70%',
        marginLeft: 'calc(15% - 60px)'
    },
    roleLabel: {
        color: '#78909c',
        fontSize: '12px',
        textAlign: 'center',
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: '-4px'
    }
}))

type UserOptionType = ActiveUser | PendingUserType

type UserListProps = {
    users: { [key: string]: UserOptionType }
    orgId: string
    role?: string
}

export type ActiveUser = OrganizationUserType & {
    photoURL?: string
    organizationRole?: {
        roleId: string
        roleName: string
    }
}

export type RowUserType = {
    id: string
    uid: string
    name: string
    email: string
    roles: string
    permissions?: number
    rolesNumber: number
    photoURL: string
    sentAt: number
    status: string
    viewerOnlyAfter?: firebase.firestore.Timestamp
}

export type PendingUserType = Omit<ActiveUser, 'organizationRole'> &
    Partial<{
        uid: string
        displayName: string
        permissions: number
    }> &
    Required<{ sentAt: number }>

function UserList({ users = {}, orgId }: UserListProps) {
    const classes = useStyles()
    const { isPrivateAccount, uid } = useAuth()

    const [sortModel, setSortModel] = useState<Array<GridSortItem>>([
        {
            field: 'name',
            sort: 'asc'
        }
    ])

    const [selectedUser, setSelectedUser] = useState<RowUserType | null>(null)

    const [deleteId, setDeleteId] = useState<string | null>(null)

    const [pageSize, setPageSize] = useState<number>(50)

    const { isAdmin } = useRoles(orgId)

    const { showSuccessNotification, showErrorNotification } = useNotification()
    const { name: orgName } = useOrganization()
    const { userSeatsQuota, usedUsersSeats } = useServiceUsage()

    const handleRemove = (uid: string | null) => {
        if (uid) {
            removeUserFromOrg({
                orgId,
                docId: uid,
                isPending: 'sentAt' in users[uid]
            })
                .then(() => setDeleteId(null))
                .then(() => showSuccessNotification('User was removed'))
                .catch(() => showErrorNotification('Something went wrong'))
        }
    }

    const $invite = useDataMutation('/b/v1/userInvite', 'POST', {
        onSuccess: () => {
            showSuccessNotification('Invite has been sent')
            logToAnalytics('send_invite_again')
        }
    })

    const inviteMutate = $invite.mutate

    const handleSendInvite = useCallback(
        async (email: string, permissions: number) => {
            if (!orgName) return

            const invite = {
                emails: email,
                message: getInviteDefaults(orgName, isPrivateAccount).message,
                ...(isAdmin ? { permissions } : {})
            }

            inviteMutate(invite).then(() => {
                logToAnalytics('send_invite', { type: 'auto reminder' })
                updatePendingUserInvitationTime(orgId, email)
            })
        },
        [inviteMutate, isAdmin, isPrivateAccount, orgId, orgName]
    )

    const disableRemove = useCallback(
        (user?: UserOptionType) => {
            if (!user || !isAdmin || user.uid === uid) return true

            return false
        },
        [isAdmin, uid]
    )

    const addActions = useMemo(() => {
        if (!isAdmin) return []

        return {
            field: 'action',
            headerName: 'Action',
            type: 'actions',
            renderCell: ({ row }: GridCellParams) => (
                <ListItem style={{ justifyContent: 'flex-end' }}>
                    {row.status === 'pending' && (
                        <Tooltip arrow placement="top" title="Resend invite">
                            <IconButton
                                size="small"
                                onClick={e => {
                                    e.stopPropagation()
                                    row.email &&
                                        handleSendInvite(row.email, row.roles?.a || ROLE.MEMBER)
                                }}
                            >
                                <Email />
                            </IconButton>
                        </Tooltip>
                    )}
                    <IconButton
                        size="small"
                        disabled={disableRemove(users[row.id])}
                        onClick={e => {
                            e.stopPropagation()
                            setDeleteId(row.id)
                        }}
                    >
                        <Delete />
                    </IconButton>
                </ListItem>
            )
        }
    }, [disableRemove, handleSendInvite, isAdmin, users])

    const columns = useMemo(
        () => [
            {
                field: 'name',
                headerName: 'Name',
                sortDirection: 'asc',
                flex: 1,
                renderCell: ({ row }: GridCellParams) => (
                    <ListItem>
                        <ListItemAvatar>
                            <Avatar src={row.photoURL} />
                        </ListItemAvatar>
                        <ListItemText>{row.name}</ListItemText>
                    </ListItem>
                )
            },
            {
                field: 'email',
                headerName: 'Email',
                sortable: true,
                flex: 1,
                renderCell: ({ row }: GridCellParams) => <ListItemText>{row.email}</ListItemText>
            },
            {
                field: 'roles',
                headerName: 'Role',
                sortable: true,
                flex: 1,
                renderCell: ({ row }: GridCellParams & { row: RowUserType }) => (
                    <ListItemText style={{ position: 'relative' }}>
                        {row.roles}
                        {row.viewerOnlyAfter && (
                            <Typography component="span" className={classes.roleLabel}>
                                viewer starting{' '}
                                {moment(row.viewerOnlyAfter.toMillis()).format('DD MMM YYYY')}
                            </Typography>
                        )}
                    </ListItemText>
                )
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 1,
                renderCell: ({ row }: GridCellParams) => {
                    return <ListItemText>{row.status}</ListItemText>
                }
            },
            ...[addActions]
        ],
        [addActions, classes.roleLabel]
    )

    const rows: Array<RowUserType> = useMemo(() => {
        return Object.values(users)
            .filter(Boolean)
            .map(r => {
                const permissions = 'permissions' in r ? r.permissions : 0
                const roles = r?.roles?.a || permissions || 0

                return {
                    id: r.uid || r.email, // for data grid
                    uid: r.uid || r.email, // for edit permissions
                    name: r.displayName || r.email,
                    email: r.email,
                    roles: getRoleName(roles)?.name,
                    rolesNumber: roles,
                    permissions,
                    photoURL: 'photoURL' in r ? r.photoURL : '',
                    sentAt: 'sentAt' in r ? r.sentAt : 0,
                    status: 'sentAt' in r ? 'pending' : 'active',
                    viewerOnlyAfter: r.viewerOnlyAfter
                }
            })
    }, [users])

    return (
        <>
            <DataGrid
                autoHeight
                pageSize={pageSize}
                showColumnRightBorder={false}
                onPageSizeChange={a => setPageSize(a)}
                className={classes.grid}
                disableColumnMenu
                sortingOrder={['asc', 'desc']}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                columns={columns as GridColumns}
                onRowClick={({ row }) => setSelectedUser(row as RowUserType)}
                disableSelectionOnClick
                rows={rows}
            />
            {selectedUser && (
                <PermissionsDialog
                    user={selectedUser}
                    onClose={() => setSelectedUser(null)}
                    isRoleUpgradeDisabled={Boolean(
                        userSeatsQuota && usedUsersSeats >= userSeatsQuota
                    )}
                />
            )}
            <ConfirmationDialog
                isOpen={Boolean(deleteId)}
                onClose={() => setDeleteId(null)}
                onConfirm={() => handleRemove(deleteId)}
                title="Are you sure?"
                text={
                    <>
                        Remove user{' '}
                        <Typography component="b" style={{ fontWeight: 600 }}>
                            {deleteId && (users[deleteId]?.displayName || users[deleteId]?.email)}
                        </Typography>{' '}
                        ?
                    </>
                }
            />
        </>
    )
}

type UsersTabProps = {
    orgId: string
    usersExceeded: boolean
    orgUsers: Record<string, OrganizationUserType>
}

export const UsersTab = ({ orgId, usersExceeded, orgUsers }: UsersTabProps) => {
    const classes = useStyles()

    const { isAdmin } = useRoles()

    const [text, setText] = useState('')

    useFirestoreConnect({
        collection: isAdmin ? `usersPerOrg/${orgId}/pendingUsers` : 'empty',
        storeAs: 'pendingUsers'
    })

    const pendingUsers = useSelector(state => state.firestore.data.pendingUsers)

    const allUsersValues: Array<ActiveUser | PendingUserType> = useMemo(() => {
        const approvedUsersValues = Object.values<ActiveUser>(orgUsers || {})

        const pendingUsersValues = Object.values<PendingUserType>(pendingUsers || {}).filter(
            Boolean
        )

        return [...approvedUsersValues, ...pendingUsersValues]
    }, [orgUsers, pendingUsers])

    const filteredUsers = useMemo(() => {
        return allUsersValues.reduce((acc, curr) => {
            let currentObj = {}
            if (curr.displayName && curr.uid) {
                if (curr.displayName.toLowerCase().includes(text.toLowerCase())) {
                    currentObj = { [curr.uid]: curr }
                }
            } else {
                if (curr.email.toLowerCase().includes(text.toLowerCase())) {
                    currentObj = { [curr.email]: curr }
                }
            }
            return { ...acc, ...currentObj }
        }, {})
    }, [text, allUsersValues])

    return (
        <>
            {usersExceeded && (
                <Box mb={4}>
                    <ExceededUsersNotification inline />
                </Box>
            )}
            <Box className={classes.searchAndUsersAmount}>
                <Box width="60px" textAlign="center" pb={2} pr={2}>
                    <Typography variant="h2" gutterBottom className={classes.title}>
                        {Object.keys(filteredUsers || {}).length}
                    </Typography>
                    <Typography className={classes.subTitle} variant="h5" component="h2">
                        Total Users
                    </Typography>
                </Box>
                <SearchField placeholder="Search" text={text} setText={setText} />
            </Box>
            {isAdmin ? (
                <UserList users={filteredUsers || {}} orgId={orgId} />
            ) : (
                <Typography variant="body1">
                    User administration permissions verification ...
                </Typography>
            )}
        </>
    )
}
